/**
 * Modal "Detalhes do Veículo" acionado ao clicar num prefixo em
 * /app/telemetria-frota. Mostra:
 *   - KPIs completos do snapshot atual (SOC, autonomia, potência, temperaturas)
 *   - Mapa Leaflet com posição atual + rastro (polyline) dos últimos 30 minutos
 *   - Ícone customizado do ônibus nas cores do portal (--nx-orange-500)
 *
 * Google Maps: ativado quando `window.__NXVOLT_GMAPS_KEY` está definido no
 * runtime (injetável via /app/settings ou meta tag). Sem key, o Leaflet
 * assume — mesmo tile provider que MapaFrotaPage. Upgrade "3D bus icon
 * realista" fica gated por essa key (versão paga).
 *
 * O componente é lazy: importa `leaflet` só quando abre pra evitar penalizar
 * bundle inicial de quem não usa a tela.
 */
import { useEffect, useMemo, useRef, useState } from "react";

interface VehicleSnapshot {
  local_prefix: string | null;
  chassi_vin: string;
  first_seen: string | null;
  last_seen: string | null;
  msg_count: number | null;
  updated_at: string | null;
  soc_pct: number | null;
  range_km: number | null;
  energy_available_kwh: number | null;
  cell_temp_avg: number | null;
  cell_temp_max: number | null;
  speed_kmh: number | null;
  odometer_km: number | null;
  ignition: boolean | null;
  handbrake: boolean | null;
  gps_lat: number | null;
  gps_lon: number | null;
  gps_speed_kmh: number | null;
  gps_sats: number | null;
  gps_course: number | null;
  plug_charging: boolean | null;
  charging_power_kw: number | null;
  motor_torque_nm: number | null;
  air_temp_outside: number | null;
  air_temp_inside: number | null;
  battery_12v_voltage: number | null;
}

interface TrailPoint {
  ts: string;
  lat: number;
  lon: number;
  chargingPowerKw: number | null;
}

interface DetailResponse {
  vehicle: VehicleSnapshot;
  trail: TrailPoint[];
  trailMinutes: number;
}

interface Props {
  vin: string;
  onClose: () => void;
}

declare global {
  interface Window {
    __NXVOLT_GMAPS_KEY?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    L?: any;
  }
}

function fmt(n: number | null | undefined, decimals = 1, suffix = ""): string {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(decimals)}${suffix}`;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function socColor(soc: number | null): string {
  if (soc === null) return "var(--nx-mute)";
  if (soc < 20) return "var(--nx-danger)";
  if (soc < 50) return "var(--nx-warn)";
  return "var(--nx-success)";
}

/**
 * SVG do ônibus com cores do portal — usado como ícone customizado no Leaflet.
 * Versão paga (3D realista via Marker3D/glTF) fica gated pela API key do
 * Google Maps + Photorealistic 3D Tiles. Este SVG é o fallback padrão.
 */
function busIconSvgHtml(orangeVar: string): string {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1.2" flood-opacity="0.35"/>
        </filter>
      </defs>
      <g filter="url(#shadow)">
        <rect x="6" y="10" width="28" height="18" rx="4" fill="${orangeVar}" stroke="#fff" stroke-width="1.5"/>
        <rect x="8.5" y="13" width="8" height="6" rx="1" fill="#e8f4ff" opacity="0.9"/>
        <rect x="17.5" y="13" width="8" height="6" rx="1" fill="#e8f4ff" opacity="0.9"/>
        <rect x="26.5" y="13" width="6" height="6" rx="1" fill="#e8f4ff" opacity="0.9"/>
        <circle cx="13" cy="30" r="3" fill="#111"/>
        <circle cx="27" cy="30" r="3" fill="#111"/>
        <circle cx="13" cy="30" r="1.2" fill="#eee"/>
        <circle cx="27" cy="30" r="1.2" fill="#eee"/>
      </g>
    </svg>
  `.trim();
}

/** Carrega leaflet dinamicamente (usa o UMD que já vem da CDN autorizada em CSP). */
async function ensureLeaflet(): Promise<typeof window.L> {
  if (typeof window.L !== "undefined") return window.L;
  await new Promise<void>((resolve, reject) => {
    // CSS
    if (!document.querySelector('link[data-nx="leaflet"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      link.setAttribute("data-nx", "leaflet");
      document.head.appendChild(link);
    }
    // JS
    const existing = document.querySelector('script[data-nx="leaflet"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("leaflet_load_failed")));
      return;
    }
    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.setAttribute("data-nx", "leaflet");
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("leaflet_load_failed"));
    document.head.appendChild(script);
  });
  return window.L;
}

export function VehicleDetailModal({ vin, onClose }: Props) {
  const [data, setData] = useState<DetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<unknown>(null);

  // ESC fecha o modal
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Fetch dados + rastro
  useEffect(() => {
    let alive = true;
    setError(null);
    setData(null);
    fetch(`/api/telemetria-frota/veiculo/${encodeURIComponent(vin)}/detail?minutes=30`, {
      credentials: "include",
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`http_${r.status}`);
        return r.json();
      })
      .then((body: DetailResponse) => {
        if (alive) setData(body);
      })
      .catch((err) => {
        if (alive) setError((err as Error).message ?? "load_failed");
      });
    return () => {
      alive = false;
    };
  }, [vin]);

  // Boot do mapa quando dado chega
  useEffect(() => {
    if (!data || !mapContainerRef.current) return;
    let disposed = false;
    (async () => {
      try {
        const L = await ensureLeaflet();
        if (disposed || !mapContainerRef.current) return;

        // Ler cor laranja do design system para o ícone
        const orange = getComputedStyle(document.documentElement)
          .getPropertyValue("--nx-orange-500")
          .trim() || "#e8692a";

        const { vehicle, trail } = data;
        const hasCurrentPos =
          vehicle.gps_lat !== null && vehicle.gps_lon !== null;
        const points = trail.map((p) => [p.lat, p.lon] as [number, number]);
        const currentPos: [number, number] | null = hasCurrentPos
          ? [vehicle.gps_lat as number, vehicle.gps_lon as number]
          : points.length > 0
            ? points[points.length - 1]!
            : null;

        // Centro do mapa: posição atual, ou último ponto do rastro,
        // ou Sambaíba (default), ou centro do BR
        const center: [number, number] = currentPos ?? [-23.5, -46.65];

        const map = L.map(mapContainerRef.current, {
          zoomControl: true,
          scrollWheelZoom: true,
        }).setView(center, currentPos ? 15 : 5);
        mapRef.current = map;

        // CartoDB Positron: OSM bloqueou tile.openstreetmap.org por volume.
        // CartoDB tem licença permissiva pra uso comercial e não banir
        // rápido. Subdomínios a/b/c/d evitam single-host throttle.
        L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
          subdomains: 'abcd',
          maxZoom: 19,
          attribution: "© OpenStreetMap · © CARTO",
        }).addTo(map);

        // Rastro dos últimos 30min
        if (points.length >= 2) {
          L.polyline(points, {
            color: orange,
            weight: 4,
            opacity: 0.85,
            lineJoin: "round",
          }).addTo(map);
          // Ponto de início do rastro (círculo pequeno)
          L.circleMarker(points[0]!, {
            radius: 5,
            fillColor: "#fff",
            color: orange,
            weight: 2,
            fillOpacity: 1,
          })
            .addTo(map)
            .bindPopup("Início do rastro (30 min atrás)");
        }

        // Marker atual com ícone customizado do ônibus
        if (currentPos) {
          const icon = L.divIcon({
            className: "nx-bus-marker",
            html: busIconSvgHtml(orange),
            iconSize: [40, 40],
            iconAnchor: [20, 20],
          });
          // Constrói DOM ao invés de HTML string pra impedir XSS caso
          // prefix/chassi venha com caracteres HTML injetados no upstream.
          const popupEl = document.createElement("div");
          const title = document.createElement("b");
          title.textContent = String(
            vehicle.local_prefix ?? vehicle.chassi_vin,
          );
          popupEl.appendChild(title);
          popupEl.appendChild(document.createElement("br"));
          popupEl.appendChild(
            document.createTextNode(`SOC: ${fmt(vehicle.soc_pct, 1, "%")}`),
          );
          popupEl.appendChild(document.createElement("br"));
          popupEl.appendChild(
            document.createTextNode(
              `Velocidade: ${fmt(vehicle.speed_kmh, 0, " km/h")}`,
            ),
          );
          L.marker(currentPos, { icon }).addTo(map).bindPopup(popupEl).openPopup();
        }

        // Ajusta zoom pra caber rastro + posição atual
        if (points.length >= 2 || currentPos) {
          const bounds = L.latLngBounds(
            [
              ...(currentPos ? [currentPos] : []),
              ...points,
            ] as [number, number][],
          );
          if (bounds.isValid()) {
            map.fitBounds(bounds.pad(0.2), { maxZoom: 17 });
          }
        }
      } catch (err) {
        if (!disposed) setError((err as Error).message ?? "map_boot_failed");
      }
    })();
    return () => {
      disposed = true;
      const map = mapRef.current as { remove?: () => void } | null;
      if (map && typeof map.remove === "function") {
        map.remove();
      }
      mapRef.current = null;
    };
  }, [data]);

  const title = useMemo(() => {
    if (!data) return `Veículo ${vin}`;
    const v = data.vehicle;
    return v.local_prefix ? `Ônibus ${v.local_prefix}` : v.chassi_vin;
  }, [data, vin]);

  return (
    <div
      className="nx-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="nx-vehicle-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="nx-modal__card"
        style={{ maxWidth: 1000, width: "min(96vw, 1000px)" }}
      >
        <div className="nx-modal__head">
          <h2 id="nx-vehicle-modal-title" style={{ margin: 0 }}>
            {title}
            {data?.vehicle.chassi_vin && (
              <span
                className="mono"
                style={{
                  marginLeft: 12,
                  fontSize: ".75rem",
                  fontWeight: 400,
                  color: "var(--nx-mute)",
                }}
              >
                {data.vehicle.chassi_vin}
              </span>
            )}
          </h2>
          <button
            className="nx-modal__close"
            onClick={onClose}
            aria-label="Fechar"
            type="button"
          >
            ×
          </button>
        </div>

        <div className="nx-modal__body" style={{ display: "grid", gap: 14 }}>
          {error && (
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                background: "var(--nx-danger-50, var(--nx-neutral-50))",
                color: "var(--nx-danger)",
                fontSize: ".9rem",
              }}
            >
              Falha ao carregar dados do veículo: {error}
            </div>
          )}

          {!error && !data && (
            <div style={{ padding: 32, textAlign: "center", color: "var(--nx-mute)" }}>
              Carregando telemetria…
            </div>
          )}

          {data && (
            <>
              {/* KPIs primários */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: 8,
                }}
              >
                <Kpi
                  label="SOC"
                  value={fmt(data.vehicle.soc_pct, 1, "%")}
                  color={socColor(data.vehicle.soc_pct)}
                />
                <Kpi label="Autonomia" value={fmt(data.vehicle.range_km, 0, " km")} />
                <Kpi label="Velocidade" value={fmt(data.vehicle.speed_kmh, 0, " km/h")} />
                <Kpi label="Odômetro" value={fmt(data.vehicle.odometer_km, 0, " km")} />
                <Kpi label="Temp. célula" value={fmt(data.vehicle.cell_temp_avg, 1, " °C")} />
                <Kpi
                  label="Estado"
                  value={
                    data.vehicle.plug_charging
                      ? "Carregando"
                      : data.vehicle.ignition
                        ? "Rodando"
                        : "Parado"
                  }
                />
              </div>

              {/* Mapa */}
              <div
                style={{
                  border: "1px solid var(--nx-line)",
                  borderRadius: 8,
                  overflow: "hidden",
                  height: 340,
                  position: "relative",
                }}
              >
                <div
                  ref={mapContainerRef}
                  style={{ width: "100%", height: "100%" }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    background: "var(--nx-bg)",
                    border: "1px solid var(--nx-line)",
                    borderRadius: 6,
                    padding: "4px 8px",
                    fontSize: ".75rem",
                    color: "var(--nx-mute)",
                    zIndex: 500,
                  }}
                >
                  Rastro: {data.trail.length} pontos · últimos {data.trailMinutes} min
                </div>
              </div>

              {/* Metadados */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 6,
                  fontSize: ".85rem",
                  padding: 12,
                  background: "var(--nx-neutral-50)",
                  borderRadius: 8,
                }}
              >
                <Meta label="Chassi" value={data.vehicle.chassi_vin} mono />
                <Meta label="Ignição" value={data.vehicle.ignition ? "Ligada" : "Desligada"} />
                <Meta label="Freio de mão" value={data.vehicle.handbrake ? "Puxado" : "Solto"} />
                <Meta
                  label="Potência carga"
                  value={fmt(data.vehicle.charging_power_kw, 1, " kW")}
                />
                <Meta label="Torque motor" value={fmt(data.vehicle.motor_torque_nm, 0, " Nm")} />
                <Meta label="Temp. externa" value={fmt(data.vehicle.air_temp_outside, 1, " °C")} />
                <Meta label="Temp. interna" value={fmt(data.vehicle.air_temp_inside, 1, " °C")} />
                <Meta label="Bateria 12V" value={fmt(data.vehicle.battery_12v_voltage, 2, " V")} />
                <Meta label="Satélites GPS" value={data.vehicle.gps_sats?.toString() ?? "—"} />
                <Meta label="Atualizado" value={fmtDateTime(data.vehicle.updated_at)} />
                <Meta label="Primeira msg" value={fmtDateTime(data.vehicle.first_seen)} />
                <Meta
                  label="Mensagens"
                  value={data.vehicle.msg_count?.toString() ?? "—"}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                }}
              >
                <button className="nx-btn" type="button" onClick={onClose}>
                  Fechar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 8,
        border: "1px solid var(--nx-line)",
        background: "var(--nx-bg)",
      }}
    >
      <div
        style={{
          fontSize: ".7rem",
          textTransform: "uppercase",
          letterSpacing: ".04em",
          color: "var(--nx-mute)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "1.1rem",
          fontWeight: 700,
          color: color ?? "var(--nx-ink)",
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Meta({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <span style={{ color: "var(--nx-mute)", marginRight: 6 }}>{label}:</span>
      <span
        className={mono ? "mono" : undefined}
        style={{ color: "var(--nx-ink)" }}
      >
        {value}
      </span>
    </div>
  );
}
